import React, { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp, HelpCircle } from 'lucide-react'

export default function FAQSection() {
  const [faqs, setFaqs] = useState([])
  const [openIndex, setOpenIndex] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/faq_items')
        if (response.ok) {
          const data = await response.json()
          // Sort by order field
          const sorted = data.sort((a, b) => (a.order || 0) - (b.order || 0))
          setFaqs(sorted)
        } else {
          throw new Error('Failed to fetch FAQs')
        }
      } catch (err) {
        console.error('Error fetching FAQs:', err)
        // Fallback FAQs if fetch fails
        setFaqs([
          {
            question: { da: 'Hvor meget koster et uforpligtende tilbud på malerarbejde?' },
            answer: { da: '<p>Hos Malerfirma Villy Poulsen ApS tilbyder vi altid et <strong>100% gratis og uforpligtende tilbud</strong> på din maleropgave. Vi drøfter dine ønsker og behov, og giver dig et klart overblik over prisen på den ønskede opgave.</p>' }
          },
          {
            question: { da: 'Hvilke typer maling anvender I?' },
            answer: { da: '<p>Vi vægter kvalitet og miljø højt. Derfor benytter vi udelukkende <strong>vandbaseret maling</strong> af højeste kvalitet for at sikre det smukkeste, mest holdbare og mest miljøvenlige resultat for dit hjem eller din virksomhed.</p>' }
          },
          {
            question: { da: 'Udfører I malerarbejde for både private og erhverv?' },
            answer: { da: '<p>Ja, vi har over 25 års erfaring med at udføre malerarbejde for både <strong>private kunder, erhvervskunder, boligforeninger, private boligselskaber, institutioner og offentlige instanser</strong> i Østjylland.</p>' }
          },
          {
            question: { da: 'Hvad er sprøjtemaling, og hvad kan sprøjtemales?' },
            answer: { da: '<p>Sprøjtemaling er en avanceret teknik, der sikrer en fuldstændig glat og ensartet overflade uden penselstrøg. Vi sprøjtemaler blandt andet <strong>døre, dørkarme, fodlister, skabslåger, møbler, lofter og vægge</strong>. Vi sprøjtemaler også kilometervis af beklædningsbrædder med vores avancerede Ceetec 40 malermaskine.</p>' }
          },
          {
            question: { da: 'Hvornår på året kan man få malet sit hus udvendigt?' },
            answer: { da: '<p>Udvendigt malerarbejde og facademaling udføres bedst i tørre perioder i sommerhalvåret (typisk fra maj til september), hvor luftfugtigheden er lav, og temperaturen er stabil over 10 grader. Kontakt os i god tid for at aftale en tidsplan for dit udendørs malerarbejde.</p>' }
          },
          {
            question: { da: 'Kan I hjælpe med at koordinere andre håndværkere?' },
            answer: { da: '<p>Ja, vi har mange dygtige samarbejdspartnere og er gerne behjælpelige med at kontakte andre håndværkere samt fungere som <strong>tovholder på dit projekt</strong>, så du kan være sikker på, at hele forløbet kører glat, effektivt og koordineret.</p>' }
          }
        ])
      } finally {
        setLoading(false)
      }
    }

    fetchFaqs()
  }, [])

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <section className="py-16 bg-gray-50 border-t border-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12">
          <span className="text-xs font-bold uppercase tracking-widest text-[#01529E] bg-[#01529E]/5 px-3 py-1.5 rounded-full">
            Ofte Stillede Spørgsmål
          </span>
          <h2 className="text-3xl font-black text-gray-900 mt-3">
            Spørgsmål & Svar om malerarbejde
          </h2>
          <p className="text-gray-600 mt-2 text-sm max-w-xl mx-auto">
            Her finder du svar på nogle af de mest almindelige spørgsmål vedrørende priser, materialer, tidsplaner og koordinering.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#01529E]" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            {/* Left Column */}
            <div className="space-y-4">
              {faqs.filter((_, i) => i % 2 === 0).map((faq) => {
                const globalIndex = faqs.indexOf(faq)
                const isOpen = openIndex === globalIndex
                const questionText = faq.question?.da || faq.question
                const answerHtml = faq.answer?.da || faq.answer

                return (
                  <div
                    key={globalIndex}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden transition-all duration-300"
                  >
                    <button
                      onClick={() => toggleFaq(globalIndex)}
                      className="w-full min-h-[88px] px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <span className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2.5">
                        <HelpCircle className="h-5 w-5 text-[#01529E] shrink-0" />
                        {questionText}
                      </span>
                      <span className="p-1 rounded-lg bg-gray-100 text-gray-500 shrink-0">
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-5 pt-1 border-t border-gray-50 text-gray-600 text-sm leading-relaxed animate-in fade-in duration-200">
                        <div
                          dangerouslySetInnerHTML={{ __html: answerHtml }}
                          className="prose prose-sm max-w-none prose-red"
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {faqs.filter((_, i) => i % 2 !== 0).map((faq) => {
                const globalIndex = faqs.indexOf(faq)
                const isOpen = openIndex === globalIndex
                const questionText = faq.question?.da || faq.question
                const answerHtml = faq.answer?.da || faq.answer

                return (
                  <div
                    key={globalIndex}
                    className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden transition-all duration-300"
                  >
                    <button
                      onClick={() => toggleFaq(globalIndex)}
                      className="w-full min-h-[88px] px-6 py-4 text-left flex items-center justify-between gap-4 hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <span className="font-bold text-gray-900 text-sm sm:text-base flex items-center gap-2.5">
                        <HelpCircle className="h-5 w-5 text-[#01529E] shrink-0" />
                        {questionText}
                      </span>
                      <span className="p-1 rounded-lg bg-gray-100 text-gray-500 shrink-0">
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </span>
                    </button>

                    {isOpen && (
                      <div className="px-6 pb-5 pt-1 border-t border-gray-50 text-gray-600 text-sm leading-relaxed animate-in fade-in duration-200">
                        <div
                          dangerouslySetInnerHTML={{ __html: answerHtml }}
                          className="prose prose-sm max-w-none prose-red"
                        />
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
